import PropTypes from 'prop-types';
import React from 'react';
import { ProductImage } from '../../fuse-product-image/src/product-image';
import { PromoIdentifier } from '../../fuse-promo-identifier/src/promo-identifier';
import { Rating } from '../../fuse-rating/src/rating';
import ColourSwatch from './colour-swatch';
import { imageSources } from './config';
import SkeletonCardComponent from './skeleton-card';
import {
	BrandContainer,
	CardContainer,
	SpaceUnderImage,
	StyledPrice,
	StyledProductcard,
	StyledSaveForLater,
	TaggstarContainer,
	TaggstarWrapper,
	TitleContainer,
} from './styled-product-card';

const { useMemo, useCallback, useState } = React;

const propTypes = {
	catalogueId: PropTypes.string,
	title: PropTypes.string,
	brand: PropTypes.string,
	productUrl: PropTypes.string,
	isSkeleton: PropTypes.bool,
	image: PropTypes.shape({
		url: PropTypes.string,
		altText: PropTypes.string,
		isAltReal: PropTypes.bool,
		disabledLazy: PropTypes.bool,
		fetchPriority: PropTypes.oneOf(['low', 'auto', 'high']),
	}),
	price: PropTypes.shape({
		current: PropTypes.number,
		showCurrentPriceFrom: PropTypes.bool,
		previous: PropTypes.number,
		showPreviousPriceFrom: PropTypes.bool,
		saving: PropTypes.number,
		showSave: PropTypes.bool,
		isEuro: PropTypes.bool,
	}),
	sfl: PropTypes.shape({
		isSaved: PropTypes.bool,
		saveCallback: PropTypes.func,
	}),
	stars: PropTypes.shape({
		rating: PropTypes.number,
		reviewText: PropTypes.string,
	}),
	swatches: PropTypes.arrayOf(
		PropTypes.shape({
			displayName: PropTypes.string,
			imageSrc: PropTypes.string,
		}),
	),
	promotionIdentifier: PropTypes.shape({
		displayName: PropTypes.string,
		colour: PropTypes.oneOf(['brand', 'black', 'featured', '']),
	}),
	dataTestId: PropTypes.string,
	className: PropTypes.string,
};

const defaultProps = {
	catalogueId: '',
	title: '',
	brand: '',
	productUrl: '#',
	isSkeleton: false,
	image: {
		url: undefined,
		altText: undefined,
		isAltReal: false,
		disabledLazy: false,
		fetchPriority: undefined,
	},
	price: {
		current: 0,
		showCurrentPriceFrom: false,
		previous: 0,
		showPreviousPriceFrom: false,
		saving: 0,
		showSave: true,
		isEuro: false,
	},
	sfl: {
		saveCallback: null,
		isSaved: false,
	},
	stars: {
		rating: 0,
		reviewText: '',
	},
	swatches: [],
	promotionIdentifier: {
		displayName: '',
		colour: 'black',
	},
	dataTestId: null,
	className: undefined,
};

const isAltExists = (text) =>
	(typeof text === 'string' || text instanceof String) && text.length > 1;

function ComplexProductcard({
	catalogueId,
	title,
	brand,
	productUrl,
	isSkeleton,
	image,
	price,
	sfl,
	stars,
	swatches,
	promotionIdentifier,
	dataTestId,
	className,
	...props
}) {
	const [isSavePressed, setSavePressed] = useState(sfl?.isSaved);
	const imgAlt = !isAltExists(image?.altText) ? `Missing image for: ${title}` : image?.altText;


	const promoIdentifierSize = useMemo(() => ({ xxs: 'm2', m: 'm1', l: '1' }), []);
	const textSize = useMemo(() => ({ xxs: 'm2', m: 'm1', l: '1' }), []);
	const priceSize = useMemo(() => ({ xxs: 'xs', m: 'xs', l: 's' }), []);
	const ratingSize = useMemo(() => ({ xxs: 'small', m: 'medium', l: 'large' }), []);
	const sflIconSize = useMemo(() => ({ xxs: 'small', m: 'medium' }), []);

	const handleSaveForLater = useCallback(
		(e, _, newState) => {
			e.preventDefault();
			setSavePressed(newState);
			sfl?.saveCallback(newState);
		},
		[sfl?.saveCallback],
	);

	return isSkeleton ? (
		<SkeletonCardComponent />
	) : (
		<CardContainer
			className={className}
			data-testid={dataTestId || 'fuse-complex-product-card-data-testid'}
			data-tagg-id={catalogueId || undefined}
			{...props}
		>
			<StyledProductcard href={productUrl}>
				<ProductImage
					alt={imgAlt}
					src={image?.url}
					lazy={!image?.disabledLazy}
					isAltReal={image?.isAltReal}
					sources={imageSources}
					fetchPriority={image?.fetchPriority}
					border="var(--border-width-xs) solid var(--color-global-grey300)"
				/>
				{promotionIdentifier?.displayName && (
					<PromoIdentifier
						text={promotionIdentifier.displayName}
						className="fuse-complex-product-card__promo-identifier"
						color={promotionIdentifier.colour}
						dataTestId="fuse-complex-product-card__promo-identifier"
						size={promoIdentifierSize}
					/>
				)}
				<SpaceUnderImage />
				<ColourSwatch swatches={swatches} />
				<TaggstarWrapper>
					<TaggstarContainer />
					<BrandContainer type="paragraph" size={textSize} tag="span">
						{brand}
					</BrandContainer>
					<TitleContainer type="heading" size={textSize} tag="h3">
						{title}
					</TitleContainer>
					<StyledPrice
						price={price?.current || 0}
						headingTag="h4"
						isEuro={price?.isEuro}
						prevPrice={price?.previous}
						saving={price?.saving}
						showCurrentFrom={price?.showCurrentPriceFrom}
						showPreviousFrom={price?.showPreviousPriceFrom}
						showSave={price?.showSave}
						size={priceSize}
					/>
					{stars?.rating ? (
						<Rating
							rating={stars?.rating}
							size={ratingSize}
							reviewLinkText={stars?.reviewText}
							dataTestId="fuse-complex-product-card__rating"
						/>
					) : null}
				</TaggstarWrapper>
			</StyledProductcard>
			{sfl?.saveCallback && typeof sfl?.saveCallback === 'function' && (
				<StyledSaveForLater
					isSaved={sfl?.isSaved}
					clickCallback={handleSaveForLater}
					showLabel={false}
					aria-pressed={isSavePressed}
					saveLabel={`Save for later ${brand} ${title}`}
					savedLabel={`Saved ${brand} ${title}`}
					iconSize={sflIconSize}
				/>
			)}
		</CardContainer>
	);
}

ComplexProductcard.propTypes = propTypes;
ComplexProductcard.defaultProps = defaultProps;

export { ComplexProductcard };

export default ComplexProductcard;
